import { Loader2, PhoneCall } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  formatCoverageCategory,
  InsuranceContact,
  useInsuranceContacts,
} from '@/hooks/useInsuranceContacts';
import { Task } from '@/services/types/client-intake-types';
import { isPhoneNumberValid } from '@/utils/validation';

const CUSTOM_OPTION = '__CUSTOM__';
const NONE_OPTION = '__NONE__';

const formatPhoneNumber = (phone: string) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return phone;
};

interface VoiceAgentInsuranceContactCardProps {
  task: Task;
  matterId: string;
  onContactNumberChange?: (value: string | null) => void;
}

/**
 * Phone number selector for Voice AI on claims/insurance tasks.
 * Shows carrier, adjuster, and subrogator contacts from the matter's coverages.
 */
export const VoiceAgentInsuranceContactCard: React.FC<
  VoiceAgentInsuranceContactCardProps
> = ({ task, matterId, onContactNumberChange }) => {
  const { carrierContacts, adjusterContacts, directoryContacts, isLoading } =
    useInsuranceContacts(matterId);

  const [selectValue, setSelectValue] = useState<string>(NONE_OPTION);
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Find suggested contact based on task's coverage if available
  const suggestedContact = useMemo(() => {
    // If task has a linked coverage, prioritize contacts from that coverage
    const coverageId = task.coverage?.id;
    if (coverageId) {
      // First try adjuster, then carrier
      const adjuster = adjusterContacts.find(
        (c) => c.coverageId === coverageId,
      );
      if (adjuster) return adjuster;

      const carrier = carrierContacts.find((c) => c.coverageId === coverageId);
      if (carrier) return carrier;
    }

    // Otherwise return first available contact
    return adjusterContacts[0] || carrierContacts[0] || null;
  }, [task.coverage?.id, adjusterContacts, carrierContacts]);

  // Initialize with suggested contact
  useEffect(() => {
    if (suggestedContact && selectValue === NONE_OPTION) {
      setSelectValue(suggestedContact.phone);
      onContactNumberChange?.(suggestedContact.phone);
    }
  }, [suggestedContact, selectValue, onContactNumberChange]);

  const handleSelectChange = useCallback(
    (value: string) => {
      if (value === CUSTOM_OPTION) {
        setShowCustomInput(true);
        setCustomValue('');
        return;
      }

      setShowCustomInput(false);
      setCustomValue('');
      setSelectValue(value);

      if (value === NONE_OPTION) {
        onContactNumberChange?.(null);
      } else {
        onContactNumberChange?.(value);
      }
    },
    [onContactNumberChange],
  );

  const handleSaveCustom = useCallback(() => {
    const trimmed = customValue.trim();
    if (!trimmed || !isPhoneNumberValid(trimmed)) return;

    setSelectValue(trimmed);
    setShowCustomInput(false);
    setCustomValue('');
    onContactNumberChange?.(trimmed);
  }, [customValue, onContactNumberChange]);

  const hasContacts =
    carrierContacts.length > 0 ||
    adjusterContacts.length > 0 ||
    directoryContacts.length > 0;

  const renderContactItem = (contact: InsuranceContact) => {
    const categoryLabel = formatCoverageCategory(contact.coverageCategory);
    return (
      <SelectItem key={contact.id} value={contact.phone}>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {contact.name}
            {categoryLabel && (
              <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-700">
                {categoryLabel}
              </span>
            )}
            {contact.secondaryTitle && (
              <span className="ml-1.5 text-xs text-gray-500">
                ({contact.secondaryTitle})
              </span>
            )}
          </span>
          <span className="text-xs text-gray-500">
            {formatPhoneNumber(contact.phone)}
          </span>
        </div>
      </SelectItem>
    );
  };

  let body: React.ReactNode;

  if (isLoading) {
    body = (
      <div className="space-y-2">
        <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
        <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
      </div>
    );
  } else if (!hasContacts) {
    body = (
      <div className="space-y-3">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          No insurance contacts found. Add carrier or adjuster information to
          enable Voice AI calling, or enter a custom number below.
        </div>
        <div className="space-y-2">
          <Label
            className="text-xs text-gray-600"
            htmlFor="voice-ai-custom-phone"
          >
            Phone Number
          </Label>
          <Input
            id="voice-ai-custom-phone"
            type="tel"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder="Enter phone number (e.g. 303-555-1234)"
          />
          <Button
            size="sm"
            onClick={handleSaveCustom}
            disabled={!isPhoneNumberValid(customValue.trim())}
          >
            Use This Number
          </Button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-xs text-gray-600">Insurance Contact</Label>
          <Select
            value={showCustomInput ? CUSTOM_OPTION : selectValue}
            onValueChange={handleSelectChange}
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select a contact" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {adjusterContacts.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-gray-500">
                    Adjusters & Subrogators
                  </SelectLabel>
                  {adjusterContacts.map(renderContactItem)}
                </SelectGroup>
              )}
              {carrierContacts.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-gray-500">
                    Carriers
                  </SelectLabel>
                  {carrierContacts.map(renderContactItem)}
                </SelectGroup>
              )}
              {directoryContacts.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-gray-500">
                    Carrier Directory
                  </SelectLabel>
                  {directoryContacts.map(renderContactItem)}
                </SelectGroup>
              )}
              <SelectItem value={CUSTOM_OPTION}>Enter custom number</SelectItem>
              <SelectItem value={NONE_OPTION}>No phone number</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {showCustomInput && (
          <div className="space-y-2">
            <Label className="text-xs text-gray-600" htmlFor="voice-ai-custom">
              Custom Phone Number
            </Label>
            <Input
              id="voice-ai-custom"
              type="tel"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder="Enter phone number (e.g. 303-555-1234)"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveCustom}
                disabled={!isPhoneNumberValid(customValue.trim())}
              >
                Save Number
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomValue('');
                }}
              >
                Cancel
              </Button>
            </div>
            {customValue && !isPhoneNumberValid(customValue.trim()) && (
              <p className="text-xs text-red-600">
                Enter a valid phone number (e.g. 303-555-1234 or +12025550123)
              </p>
            )}
          </div>
        )}

        {selectValue !== NONE_OPTION && !showCustomInput && (
          <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
            <PhoneCall className="h-3.5 w-3.5" />
            <span>
              AI will call:{' '}
              <span className="font-semibold">
                {formatPhoneNumber(selectValue)}
              </span>
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            Insurance Contact
          </div>
          <div className="text-xs text-gray-500">
            Choose the phone number Voice AI should dial
          </div>
        </div>
        {isLoading && (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        )}
      </div>
      {body}
    </div>
  );
};
